import React, { useEffect, useLayoutEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import "./styles.css";
import "./i18n";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import * as serviceWorkerRegistration from './lib/serviceWorkerRegistration';
import { endStatusBarSplash } from './lib/statusBar';
import Home from "./pages/Home";
import AutoLogin from "./pages/AutoLogin";
import Layout from "./components/Layout";
import { EventsProvider } from "./contexts/EventsContext";
import { AudioTourProvider } from "./contexts/AudioTourContext";
import BookingInvalidatedDialog from "./components/BookingInvalidatedDialog";
import CheckInClaimWatcher from "./components/CheckInClaimWatcher";
import { Toaster } from "./components/ui/toaster";
import { requestClaimCheck } from "./lib/checkInClaimStorage";
import DesktopGate from "./components/DesktopGate";
import InstallAppManager from "./components/install/InstallAppManager";
import RouteBoundary from "./components/RouteBoundary";
import { lazyPage, preloadForPath } from "./lib/lazyPage";
import * as chunks from "./pageChunks";
import { SERVICE_PATHS } from "./lib/servicePages";

// ---- Le pagine a pezzi (lib/lazyPage) ----
// La Home resta nel file principale: è la prima schermata. Tutte le altre
// arrivano quando servono; offline le tiene il service worker (v18).
const Restaurants = lazyPage(chunks.loadRestaurants);
const Explore = lazyPage(chunks.loadExplore);
const BookNow = lazyPage(chunks.loadBookNow);
const CheckIn = lazyPage(chunks.loadCheckIn);
const ClaimBooking = lazyPage(chunks.loadClaimBooking);
const Partners = lazyPage(chunks.loadPartners);
const MolaDiBari = lazyPage(chunks.loadMolaDiBari);
const PoliganoAMare = lazyPage(chunks.loadPoliganoAMare);
const Monopoli = lazyPage(chunks.loadMonopoli);
const Bari = lazyPage(chunks.loadBari);
const Alberobello = lazyPage(chunks.loadAlberobello);
const Locorotondo = lazyPage(chunks.loadLocorotondo);
const Trani = lazyPage(chunks.loadTrani);
const CastellanaGrotte = lazyPage(chunks.loadCastellanaGrotte);
const Matera = lazyPage(chunks.loadMatera);
const Lecce = lazyPage(chunks.loadLecce);
const Zoosafari = lazyPage(chunks.loadZoosafari);
const Ostuni = lazyPage(chunks.loadOstuni);
// Le attrazioni: una pagina sola, con la configurazione della città.
const loadAttractions = chunks.loadAttractionPage;
const MolaAttraction = lazyPage(() => loadAttractions().then((m) => ({ default: () => <m.default config={m.molaDiBariConfig} /> })));
const PolignanoAttraction = lazyPage(() => loadAttractions().then((m) => ({ default: () => <m.default config={m.polignanoConfig} /> })));
const MonopoliAttraction = lazyPage(() => loadAttractions().then((m) => ({ default: () => <m.default config={m.monopoliConfig} /> })));
const BariAttraction = lazyPage(() => loadAttractions().then((m) => ({ default: () => <m.default config={m.bariConfig} /> })));
const AlberobelloAttraction = lazyPage(() => loadAttractions().then((m) => ({ default: () => <m.default config={m.alberobelloConfig} /> })));
const LocorotondoAttraction = lazyPage(() => loadAttractions().then((m) => ({ default: () => <m.default config={m.locorotondoConfig} /> })));
const TraniAttraction = lazyPage(() => loadAttractions().then((m) => ({ default: () => <m.default config={m.traniConfig} /> })));
const CastellanaGrotteAttraction = lazyPage(() => loadAttractions().then((m) => ({ default: () => <m.default config={m.castellanaConfig} /> })));
const MateraAttraction = lazyPage(() => loadAttractions().then((m) => ({ default: () => <m.default config={m.materaConfig} /> })));
const LecceAttraction = lazyPage(() => loadAttractions().then((m) => ({ default: () => <m.default config={m.lecceConfig} /> })));
const ZoosafariAttraction = lazyPage(() => loadAttractions().then((m) => ({ default: () => <m.default config={m.zoosafariConfig} /> })));
const OstuniAttraction = lazyPage(() => loadAttractions().then((m) => ({ default: () => <m.default config={m.ostuniConfig} /> })));
const MyStay = lazyPage(chunks.loadMyStay);
const Extras = lazyPage(chunks.loadExtras);
const ExtraDetail = lazyPage(chunks.loadExtraDetail);
const ExtraGroupPage = lazyPage(() => chunks.loadExtraDetail().then((m) => ({ default: m.ExtraGroupPage })));
const ExtrasCart = lazyPage(chunks.loadExtrasCart);
const Laundry = lazyPage(chunks.loadLaundry);
const GroupCheckIn = lazyPage(chunks.loadGroupCheckIn);
const GiftCards = lazyPage(chunks.loadGiftCards);
const MyGiftCards = lazyPage(chunks.loadMyGiftCards);
// La Fuga romantica a tutto schermo (prima una finestra sopra la Home).
const RomanticEscape = lazyPage(chunks.loadRomanticEscape);
const PaymentSuccess = lazyPage(chunks.loadPaymentSuccess);
const Parking = lazyPage(chunks.loadParking);
const TravelNotes = lazyPage(chunks.loadTravelNotes);
const TravelWallet = lazyPage(chunks.loadTravelWallet);
const Events = lazyPage(chunks.loadEvents);
// "Tutte le gite" (da "Vedi tutte" sotto le gite di Esplora).
const Trips = lazyPage(chunks.loadTrips);
const Beaches = lazyPage(chunks.loadBeaches);
const Pharmacy = lazyPage(chunks.loadPharmacy);
const AudioTourPlayer = lazyPage(chunks.loadAudioTourPlayer);
// I servizi della Home a tutto schermo (prima finestre sopra la Home).
const ServiceRecycle = lazyPage(chunks.loadServiceRecycle);
const ServiceEmergency = lazyPage(chunks.loadServiceEmergency);
const ServiceDelivery = lazyPage(chunks.loadServiceDelivery);
const ServiceExcursions = lazyPage(chunks.loadServiceExcursions);
const ServiceExcursionPlanner = lazyPage(chunks.loadServiceExcursionPlanner);
const ServiceMassage = lazyPage(chunks.loadServiceMassage);
const ServiceRentCar = lazyPage(chunks.loadServiceRentCar);
const ServiceRentBike = lazyPage(chunks.loadServiceRentBike);
const ServiceGame = lazyPage(chunks.loadServiceGame);
const ServiceTarallo = lazyPage(chunks.loadServiceTarallo);
const ServiceSapori = lazyPage(chunks.loadServiceSapori);
const ServicePolpo = lazyPage(chunks.loadServicePolpo);

/** Il pezzo del percorso aperto si chiede durante lo splash (dal più specifico). */
const ROUTE_PRELOADS: Array<[string, { preload: () => Promise<void> }]> = [
  ["/my-stay", MyStay],
  ["/extras/cart", ExtrasCart],
  ["/extras/group/", ExtraGroupPage],
  ["/extras/", ExtraDetail],
  ["/extras", Extras],
  ["/check-in", CheckIn],
  ["/group-check-in", GroupCheckIn],
  ["/claim-booking", ClaimBooking],
  ["/explore", Explore],
  ["/restaurants", Restaurants],
  ["/partners", Partners],
  ["/events", Events],
  ["/gite", Trips],
  ["/spiagge", Beaches],
  ["/pharmacy", Pharmacy],
  ["/book", BookNow],
  ["/laundry", Laundry],
  ["/gift-card", GiftCards],
  ["/my-gift-cards", MyGiftCards],
  ["/offers/romantic", RomanticEscape],
  [SERVICE_PATHS.recycle, ServiceRecycle],
  [SERVICE_PATHS.emergency, ServiceEmergency],
  [SERVICE_PATHS.delivery, ServiceDelivery],
  [SERVICE_PATHS.excursionPlanner, ServiceExcursionPlanner],
  [SERVICE_PATHS.excursions, ServiceExcursions],
  [SERVICE_PATHS.massage, ServiceMassage],
  [SERVICE_PATHS.rentCar, ServiceRentCar],
  [SERVICE_PATHS.rentBike, ServiceRentBike],
  [SERVICE_PATHS.game, ServiceGame],
  [SERVICE_PATHS.tarallo, ServiceTarallo],
  [SERVICE_PATHS.sapori, ServiceSapori],
  [SERVICE_PATHS.polpo, ServicePolpo],
  ["/payment-success", PaymentSuccess],
  ["/parking", Parking],
  ["/travel-notes", TravelNotes],
  ["/travel-wallet", TravelWallet],
  ["/audio-tour/", AudioTourPlayer],
  ["/cities/mola-di-bari/attraction/", MolaAttraction],
  ["/cities/polignano-a-mare/attraction/", PolignanoAttraction],
  ["/cities/monopoli/attraction/", MonopoliAttraction],
  ["/cities/bari/attraction/", BariAttraction],
  ["/cities/alberobello/attraction/", AlberobelloAttraction],
  ["/cities/locorotondo/attraction/", LocorotondoAttraction],
  ["/cities/trani/attraction/", TraniAttraction],
  ["/cities/castellana-grotte/attraction/", CastellanaGrotteAttraction],
  ["/cities/matera/attraction/", MateraAttraction],
  ["/cities/lecce/attraction/", LecceAttraction],
  ["/cities/zoosafari/attraction/", ZoosafariAttraction],
  ["/cities/ostuni/attraction/", OstuniAttraction],
  ["/cities/mola-di-bari", MolaDiBari],
  ["/cities/polignano-a-mare", PoliganoAMare],
  ["/cities/monopoli", Monopoli],
  ["/cities/bari", Bari],
  ["/cities/alberobello", Alberobello],
  ["/cities/locorotondo", Locorotondo],
  ["/cities/trani", Trani],
  ["/cities/castellana-grotte", CastellanaGrotte],
  ["/cities/matera", Matera],
  ["/cities/lecce", Lecce],
  ["/cities/zoosafari", Zoosafari],
  ["/cities/ostuni", Ostuni],
];

/** Le schede della barra in basso: a riposo, dopo lo splash, così si aprono senza attesa. */
const TAB_PAGES = [MyStay, Extras, Explore, Restaurants, CheckIn];
/**
 * Anche i servizi della Home (pezzi piccoli): erano finestre, si aprivano al
 * tocco; senza, la prima apertura aspetterebbe la rete con la Home ferma.
 */
const SERVICE_PAGES = [
  ServiceRecycle,
  ServiceEmergency,
  ServiceDelivery,
  ServiceExcursions,
  ServiceExcursionPlanner,
  ServiceMassage,
  ServiceRentCar,
  ServiceRentBike,
  ServiceGame,
  ServiceTarallo,
  ServiceSapori,
  ServicePolpo,
];
const TAB_PRELOAD_DELAY_MS = 3000;

/**
 * Le push che cambiano la consegna in corso: orario fissato, promemoria,
 * consegnato e l'aggiornamento silenzioso (solo dati). Tutte fanno
 * ricaricare il widget in cima alle pagine (`nva_delivery_update`), senza
 * togliere niente a quello che la push fa gia' (banner, chat, extra).
 */
const DELIVERY_PUSH_TYPES = ['delivery_updated', 'extra_delivery', 'extra_delivery_reminder', 'extra_delivered'];
const isDeliveryPush = (type: unknown): boolean =>
  typeof type === 'string' && DELIVERY_PUSH_TYPES.indexOf(type) >= 0;

/**
 * Lo splash sparisce e la barra di stato passa alla pagina, nello stesso
 * momento (lib/statusBar: fino a qui è blu). Di solito lo fa la prima
 * schermata dell'app (AppShell), nello stesso disegno: niente fotogramma
 * vuoto fra lo splash e la pagina.
 */
const hideSplash = (): void => {
  const splash = document.getElementById('splash-screen');
  if (splash) splash.style.display = 'none';
  endStatusBarSplash();
};
/** Se la prima schermata non arriva (errore), lo splash si toglie comunque. */
const SPLASH_FALLBACK_MS = 300;

const IframeView: React.FC<{ src: string; title: string }> = ({
  src,
  title,
}) => {
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, []);

  return (
    <div className="iframe-container" style={{
      position: 'fixed',
      // Nell'app installata su iPhone la pagina dell'iframe parte sotto la
      // sfumatura di iOS (10px, lib/statusBar): lì sopra c'è il fondo della
      // pagina, del colore della barra. Altrove 0.
      top: 'calc(var(--nva-top-gap, 0px) + var(--nva-top-edge, 0px))',
      left: 0,
      right: 0,
      bottom: 'calc(72px + env(safe-area-inset-bottom))',
      overflow: 'hidden',
      WebkitOverflowScrolling: 'touch',
    }}>
      <iframe 
        src={src} 
        title={title}
        style={{
          width: '100%',
          height: '100%',
          border: 'none',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
        }}
        scrolling="yes"
        allow="fullscreen"
      />
    </div>
  );
};

const AppContent: React.FC = () => {
  const { t } = useTranslation();
  return (
    <Layout>
      <RouteBoundary>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/restaurants" element={<Restaurants />} />
          <Route path="/explore" element={<Explore />} />
          <Route path="/events" element={<Events />} />
          <Route path="/gite" element={<Trips />} />
          <Route path="/spiagge" element={<Beaches />} />
          <Route path="/pharmacy" element={<Pharmacy />} />
          <Route path="/book" element={<BookNow />} />
          <Route path="/check-in" element={<CheckIn />} />
          <Route path="/claim-booking" element={<ClaimBooking />} />
          <Route path="/group-check-in" element={<GroupCheckIn />} />
          <Route path="/my-stay" element={<MyStay />} />
          {/* "Extra & servizi": la pagina si difende da sola — senza
              prenotazione mostra l'invito ad accedere, non un elenco vuoto. */}
          <Route path="/extras" element={<Extras />} />
          {/* Le schermate dedicate, come sul tablet: la foto grande in testa e
              sotto lo stesso modulo dell'elenco. Il gruppo (colazione, taxi) sta
              su un percorso suo — /extras/group/... — perché nessun serviceId
              si chiama "group" e cosi' le due rotte non si contendono l'URL. */}
          <Route path="/extras/cart" element={<ExtrasCart />} />
          <Route path="/extras/group/:groupId" element={<ExtraGroupPage />} />
          <Route path="/extras/:serviceId" element={<ExtraDetail />} />
          <Route path="/laundry" element={<Laundry />} />
          <Route path="/auto-login" element={<AutoLogin />} />
          <Route path="/partners" element={<Partners />} />
          <Route path="/cities/mola-di-bari" element={<MolaDiBari />} />
          <Route path="/cities/mola-di-bari/attraction/:index" element={<MolaAttraction />} />
          <Route path="/cities/polignano-a-mare" element={<PoliganoAMare />} />
          <Route path="/cities/polignano-a-mare/attraction/:index" element={<PolignanoAttraction />} />
          <Route path="/cities/monopoli" element={<Monopoli />} />
          <Route path="/cities/monopoli/attraction/:index" element={<MonopoliAttraction />} />
          <Route path="/cities/bari" element={<Bari />} />
          <Route path="/cities/bari/attraction/:index" element={<BariAttraction />} />
          <Route path="/cities/alberobello" element={<Alberobello />} />
          <Route path="/cities/alberobello/attraction/:index" element={<AlberobelloAttraction />} />
          <Route path="/cities/locorotondo" element={<Locorotondo />} />
          <Route path="/cities/locorotondo/attraction/:index" element={<LocorotondoAttraction />} />
          <Route path="/cities/trani" element={<Trani />} />
          <Route path="/cities/trani/attraction/:index" element={<TraniAttraction />} />
          <Route path="/cities/castellana-grotte" element={<CastellanaGrotte />} />
          <Route path="/cities/castellana-grotte/attraction/:index" element={<CastellanaGrotteAttraction />} />
          <Route path="/cities/matera" element={<Matera />} />
          <Route path="/cities/matera/attraction/:index" element={<MateraAttraction />} />
          <Route path="/cities/lecce" element={<Lecce />} />
          <Route path="/cities/lecce/attraction/:index" element={<LecceAttraction />} />
          <Route path="/cities/zoosafari" element={<Zoosafari />} />
          <Route path="/cities/zoosafari/attraction/:index" element={<ZoosafariAttraction />} />
          <Route path="/cities/ostuni" element={<Ostuni />} />
          <Route path="/cities/ostuni/attraction/:index" element={<OstuniAttraction />} />
          <Route path="/audio-tour/:citySlug" element={<AudioTourPlayer />} />
          <Route
            path="/taxi"
            element={
              <IframeView
                src="https://meet.brevo.com/nonnavittoria-transfer"
                title={t('app.taxiService')}
              />
            }
          />
          <Route path="/parking" element={<Parking />} />
          <Route path="/travel-notes" element={<TravelNotes />} />
          <Route path="/travel-wallet" element={<TravelWallet />} />
          <Route path="/gift-card" element={<GiftCards />} />
          <Route path="/my-gift-cards" element={<MyGiftCards />} />
          {/* ?package=weekend|week (i dettagli) e &step=request (la richiesta):
              ogni passo e' una voce, e il tasto indietro ci torna. */}
          <Route path="/offers/romantic" element={<RomanticEscape />} />
          {/* I servizi della Home, una pagina ciascuno (lib/servicePages):
              i rifiuti con ?view=guide mostrano solo la guida. */}
          <Route path={SERVICE_PATHS.recycle} element={<ServiceRecycle />} />
          <Route path={SERVICE_PATHS.emergency} element={<ServiceEmergency />} />
          <Route path={SERVICE_PATHS.delivery} element={<ServiceDelivery />} />
          <Route path={SERVICE_PATHS.excursions} element={<ServiceExcursions />} />
          <Route path={SERVICE_PATHS.excursionPlanner} element={<ServiceExcursionPlanner />} />
          <Route path={SERVICE_PATHS.massage} element={<ServiceMassage />} />
          <Route path={SERVICE_PATHS.rentCar} element={<ServiceRentCar />} />
          <Route path={SERVICE_PATHS.rentBike} element={<ServiceRentBike />} />
          <Route path={SERVICE_PATHS.game} element={<ServiceGame />} />
          <Route path={SERVICE_PATHS.tarallo} element={<ServiceTarallo />} />
          <Route path={SERVICE_PATHS.sapori} element={<ServiceSapori />} />
          <Route path={SERVICE_PATHS.polpo} element={<ServicePolpo />} />
          <Route path="/payment-success" element={<PaymentSuccess />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </RouteBoundary>
    </Layout>
  );
};

const App: React.FC = () => {
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Il pezzo della pagina aperta (es. /my-stay dal tag NFC) arriva mentre
    // gira lo splash: finito lo splash la pagina è pronta.
    preloadForPath(window.location.pathname, ROUTE_PRELOADS);

    // Safety timeout: se dopo 5 secondi loading e ancora true, forza il rendering
    const safetyTimer = setTimeout(() => {
      setLoading((current) => {
        if (current) {
          console.warn('[NVA] Safety timeout: forcing app render');
          const splash = document.getElementById('splash-screen');
          if (splash) splash.classList.add('fade-out');
          setTimeout(hideSplash, 500);
        }
        return false;
      });
    }, 5000);

    // Configura i gestori degli eventi OneSignal (protetto da try-catch)
    try {
      if (typeof window !== 'undefined' && window.OneSignal && window.OneSignalDeferred) {
        window.OneSignalDeferred.push(function(OneSignal: any) {
          try {
            // Gestione click sulle notifiche
            OneSignal.Notifications.addEventListener('click', function(event: any) {
              try {
                const { url, additionalData } = event.notification || {};

                if (additionalData?.type === 'checkin_linked' && additionalData?.token) {
                  console.log('Checkin linked notification clicked - saving token for auto-login');
                  try { localStorage.setItem('nva_pending_login_token', additionalData.token); } catch(e) {}
                } else if (additionalData?.type === 'checkin_linked') {
                  // Check-in collegato, push senza credenziali: l'app riscatta
                  // la sua richiesta (CheckInClaimWatcher). Con l'app chiusa lo
                  // fa comunque all'apertura.
                  requestClaimCheck();
                }

                // Flight push tapped: refresh the flight widget once the app is open.
                if (additionalData?.type === 'flight_delay' || additionalData?.type === 'flight_landed' || additionalData?.type === 'flight_departure_reminder') {
                  try { window.dispatchEvent(new CustomEvent('nva_flight_update', { detail: additionalData })); } catch(e) {}
                }

                // Tourist tax push tapped: MyStay reloads the booking (the url
                // carries ?open=tourist-tax so the pay sheet opens by itself).
                if (typeof additionalData?.type === 'string' && additionalData.type.indexOf('tourist_tax_') === 0) {
                  try { window.dispatchEvent(new CustomEvent('nva_tourist_tax_update', { detail: additionalData })); } catch(e) {}
                }

                // Receipt ready: MyStay reloads the booking so the receipt shows up.
                if (additionalData?.type === 'receipt_ready') {
                  try { window.dispatchEvent(new CustomEvent('nva_receipt_update', { detail: additionalData })); } catch(e) {}
                }

                // "Il check-in va rifatto": Home, Il mio soggiorno e Check-in
                // rileggono la prenotazione (check-in da fare, codice nascosto).
                if (additionalData?.type === 'checkin_rejected') {
                  try { window.dispatchEvent(new CustomEvent('nva_checkin_update', { detail: additionalData })); } catch(e) {}
                }

                // Laundry push tapped (cycle finished, wash bought, socket expired): the card refreshes.
                if (typeof additionalData?.type === 'string' && additionalData.type.indexOf('laundry_') === 0) {
                  try { window.dispatchEvent(new CustomEvent('nva_laundry_update', { detail: additionalData })); } catch(e) {}
                }

                // Extra & servizi: "il late check-out è ora disponibile" oppure
                // "extra acquistato". In entrambi i casi la sezione Extra deve
                // rileggere il catalogo: lo stato è cambiato sul server.
                if (additionalData?.type === 'late_checkout_available' || additionalData?.type === 'extra_purchased') {
                  try { window.dispatchEvent(new CustomEvent('nva_extras_update', { detail: additionalData })); } catch(e) {}
                }

                // Orario di consegna / consegnato: la lista degli extra mostra
                // il chip aggiornato (l'url porta gia' alla chat).
                if (additionalData?.type === 'extra_delivery' || additionalData?.type === 'extra_delivered') {
                  try { window.dispatchEvent(new CustomEvent('nva_extras_update', { detail: additionalData })); } catch(e) {}
                }

                // Consegna (orario, promemoria, consegnato): il widget in cima
                // alle pagine si ricarica subito.
                if (isDeliveryPush(additionalData?.type)) {
                  try { window.dispatchEvent(new CustomEvent('nva_delivery_update', { detail: additionalData })); } catch(e) {}
                }

                if (url) {
                  window.location.href = url;
                }
              } catch(e) { console.warn('OneSignal click handler error:', e); }
            });

            // Gestione notifiche in primo piano
            OneSignal.Notifications.addEventListener('foregroundWillDisplay', function(event: any) {
              try {
                const { additionalData } = event.notification || {};

                if (additionalData?.type === 'checkin_linked' && additionalData?.token) {
                  console.log('Checkin linked notification received in foreground - saving token');
                  try { localStorage.setItem('nva_pending_login_token', additionalData.token); } catch(e) {}
                  window.dispatchEvent(new CustomEvent('nva_pending_login_token_set'));
                } else if (additionalData?.type === 'checkin_linked') {
                  // Senza token (channel manager nuovo): si riscatta la richiesta.
                  requestClaimCheck();
                }

                // Consegna: il widget si ricarica sempre, prima di qualunque
                // altra decisione sul banner (qui non si sopprime niente).
                if (isDeliveryPush(additionalData?.type)) {
                  try { window.dispatchEvent(new CustomEvent('nva_delivery_update', { detail: additionalData })); } catch(e) {}
                }

                // Promemoria di consegna: in chat arriva la card, quindi la
                // chat (e il badge) si ricaricano subito. Il banner si mostra
                // comunque: e' l'avviso che la consegna sta per arrivare.
                if (additionalData?.type === 'extra_delivery_reminder') {
                  window.dispatchEvent(new CustomEvent('nva_chat_reaction', { detail: additionalData }));
                }

                // Chat reaction push: always tell the drawer to refresh. If the
                // drawer is currently open the user is already reading the
                // chat, so we suppress the foreground banner; otherwise we
                // fall through and let the standard display() path show it.
                if (additionalData?.type === 'chat_reaction') {
                  window.dispatchEvent(new CustomEvent('nva_chat_reaction', {
                    detail: {
                      messageId: additionalData?.messageId,
                      emoji: additionalData?.emoji,
                      bookingReference: additionalData?.bookingReference,
                    },
                  }));
                  if ((window as any).__nvaGuestChatOpen) {
                    try { event.preventDefault(); } catch(e) {}
                    return;
                  }
                }

                // Messaggio dello staff o orario di consegna: la chat aperta
                // si ricarica subito (senza aspettare il polling) e, se
                // l'ospite la sta gia' leggendo, niente banner sopra.
                if (!additionalData?.type || additionalData?.type === 'chat_message' || additionalData?.type === 'extra_delivery' || additionalData?.type === 'extra_delivered') {
                  window.dispatchEvent(new CustomEvent('nva_chat_reaction', { detail: additionalData || {} }));
                  if (additionalData?.type === 'extra_delivery' || additionalData?.type === 'extra_delivered') {
                    window.dispatchEvent(new CustomEvent('nva_extras_update', { detail: additionalData }));
                  }
                  if ((window as any).__nvaGuestChatOpen) {
                    try { event.preventDefault(); } catch(e) {}
                    return;
                  }
                }

                // Flight push in foreground: refresh the widget immediately, then
                // still show the banner (delay/landed/departure is worth the interruption).
                if (additionalData?.type === 'flight_delay' || additionalData?.type === 'flight_landed' || additionalData?.type === 'flight_departure_reminder') {
                  window.dispatchEvent(new CustomEvent('nva_flight_update', { detail: additionalData }));
                }

                // Tourist tax push in foreground: refresh the card right away
                // (requested → paid flips to green), then still show the banner.
                if (typeof additionalData?.type === 'string' && additionalData.type.indexOf('tourist_tax_') === 0) {
                  window.dispatchEvent(new CustomEvent('nva_tourist_tax_update', { detail: additionalData }));
                }

                if (additionalData?.type === 'receipt_ready') {
                  window.dispatchEvent(new CustomEvent('nva_receipt_update', { detail: additionalData }));
                }

                // "Il check-in va rifatto": la prenotazione si rilegge subito, e
                // il banner si mostra comunque.
                if (additionalData?.type === 'checkin_rejected') {
                  window.dispatchEvent(new CustomEvent('nva_checkin_update', { detail: additionalData }));
                }

                if (typeof additionalData?.type === 'string' && additionalData.type.indexOf('laundry_') === 0) {
                  window.dispatchEvent(new CustomEvent('nva_laundry_update', { detail: additionalData }));
                }

                // Extra & servizi in primo piano: si aggiorna subito e il
                // banner si mostra comunque (è una novità che vale l'interruzione).
                if (additionalData?.type === 'late_checkout_available' || additionalData?.type === 'extra_purchased') {
                  window.dispatchEvent(new CustomEvent('nva_extras_update', { detail: additionalData }));
                }

                setTimeout(() => {
                  try { event.notification.display(); } catch(e) {}
                }, 500);
              } catch(e) { console.warn('OneSignal foreground handler error:', e); }
            });

            // Gestione cambi di permesso
            OneSignal.Notifications.addEventListener('permissionChange', function(permission: any) {
              console.log('Notification permission changed:', permission);
            });
          } catch(e) { console.warn('OneSignal setup error:', e); }
        });
      }
    } catch(e) { console.warn('OneSignal init error:', e); }

    // La barra di stato resta blu finché c'è lo splash (lib/statusBar):
    // qui non si scrive nessun colore.
    const isInTaxiRoute = window.location.pathname === '/taxi';
    const splashScreen = document.getElementById('splash-screen');

    if (isInTaxiRoute) {
      hideSplash();
      setLoading(false);
      clearTimeout(safetyTimer);
      return;
    }

    const timer = setTimeout(() => {
      if (splashScreen) {
        splashScreen.classList.add('fade-out');
        setTimeout(() => {
          setLoading(false);
          clearTimeout(safetyTimer);
          // Lo toglie AppShell appena disegnata; questo è il paracadute.
          setTimeout(hideSplash, SPLASH_FALLBACK_MS);
        }, 500);
      } else {
        // Se splash screen non esiste, renderizza comunque l'app
        setLoading(false);
        clearTimeout(safetyTimer);
        hideSplash();
      }
    }, 2000);

    return () => {
      clearTimeout(timer);
      clearTimeout(safetyTimer);
    };
  }, []);

  // Il cambio di tema del sistema lo segue lib/statusBar.
  useEffect(() => {
    serviceWorkerRegistration.register();
  }, [loading]);

  if (loading) {
    return null;
  }

  return <AppShell />;
};

/** L'app dopo lo splash; a riposo chiede i pezzi delle schede della barra e dei servizi della Home. */
const AppShell: React.FC = () => {
  // La prima schermata dell'app è pronta: via lo splash, nello stesso disegno.
  useLayoutEffect(() => {
    hideSplash();
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      [...TAB_PAGES, ...SERVICE_PAGES].forEach((page) => { void page.preload(); });
    }, TAB_PRELOAD_DELAY_MS);
    return () => clearTimeout(timer);
  }, []);

  return (
    <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <DesktopGate>
        <EventsProvider>
          <AudioTourProvider>
            <AppContent />
            <BookingInvalidatedDialog />
            {/* Accesso automatico dopo il check-in online, e il suo avviso. */}
            <CheckInClaimWatcher />
            {/* "Installa l'app": dopo l'accesso o 20 secondi, solo su pagine tranquille. */}
            <InstallAppManager />
            <Toaster />
          </AudioTourProvider>
        </EventsProvider>
      </DesktopGate>
    </Router>
  );
};

export default App;
